import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { VscGithubInverted } from 'react-icons/vsc';
import { FiYoutube, FiSquare, FiPlay } from 'react-icons/fi';

const TRACK_META = {
  agentic:  { label: 'Agentic AI', color: '#a78bfa' },
  fullstack:{ label: 'Full-Stack', color: '#60a5fa' },
  ml:       { label: 'ML / DL',    color: '#f97316' },
  cv:       { label: 'Computer Vision', color: '#34d399' },
  mlops:    { label: 'MLOps',      color: '#facc15' },
  embedded: { label: 'Embedded',   color: '#fb7185' },
};

const PlaceholderThumb = ({ track, title }) => {
  const t = TRACK_META[track] || { color: '#888', label: '' };
  return (
    <div className="app-thumb-placeholder" style={{ borderColor: t.color + '55' }}>
      <span className="app-thumb-placeholder-icon" style={{ color: t.color }}>
        {title?.charAt(0) || '?'}
      </span>
    </div>
  );
};

const TrackBadge = ({ track }) => {
  const t = TRACK_META[track];
  if (!t) return null;
  return (
    <span className="app-track-badge" style={{ background: t.color + '22', color: t.color, borderColor: t.color + '44' }}>
      {t.label}
    </span>
  );
};

/* ── Grid media: static first frame, plays GIF on hover ── */
const GridMedia = ({ project, className, currentlyPlaying, setCurrentlyPlaying }) => {
  const [hovered, setHovered] = useState(false);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const isPlaying = isMobile ? currentlyPlaying === project.id : hovered;
  const [firstFrameUrl, setFirstFrameUrl] = useState(null);

  const gifSrc = project.gif ? `/projects/${project.id}/${project.gif}` : null;
  const thumbSrc = project.thumbnail ? `/projects/${project.id}/${project.thumbnail}` : null;

  useEffect(() => {
    if (!gifSrc) return;
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      try {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        c.getContext('2d').drawImage(img, 0, 0);
        setFirstFrameUrl(c.toDataURL('image/png'));
      } catch (_) { /* ignore tainted canvas */ }
    };
    img.src = gifSrc;
    return () => { cancelled = true; };
  }, [gifSrc]);

  if (!gifSrc && !thumbSrc) {
    return (
      <div className={className}>
        <PlaceholderThumb track={project.track} title={project.title} />
      </div>
    );
  }

  if (!gifSrc) {
    return (
      <div className={className}>
        <img src={thumbSrc} alt={project.title} onError={(e) => { e.target.style.display = 'none'; }} />
      </div>
    );
  }

  return (
    <div
      className={className}
      onMouseEnter={() => !isMobile && setHovered(true)}
      onMouseLeave={() => !isMobile && setHovered(false)}
      onClick={(e) => {
        if (isMobile) {
          e.stopPropagation();
          if (currentlyPlaying === project.id) {
            setCurrentlyPlaying(null);
          } else {
            setCurrentlyPlaying(project.id);
          }
        }
      }}
    >
      {isPlaying ? (
        <img src={gifSrc} alt={project.title} />
      ) : firstFrameUrl ? (
        <img src={firstFrameUrl} alt={project.title} />
      ) : (
        <PlaceholderThumb track={project.track} title={project.title} />
      )}
    </div>
  );
};

/* ── Media with stop/play for overview & detail views ── */
const ProjectMedia = ({ project, className }) => {
  const [playing, setPlaying] = useState(true);
  const [firstFrameUrl, setFirstFrameUrl] = useState(null);

  const gifSrc = project.gif ? `/projects/${project.id}/${project.gif}` : null;
  const thumbSrc = project.thumbnail ? `/projects/${project.id}/${project.thumbnail}` : null;
  const isGif = !!gifSrc;

  useEffect(() => {
    if (!gifSrc) return;
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      try {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        c.getContext('2d').drawImage(img, 0, 0);
        setFirstFrameUrl(c.toDataURL('image/png'));
      } catch (_) { /* ignore tainted canvas */ }
    };
    img.src = gifSrc;
    return () => { cancelled = true; };
  }, [gifSrc]);

  const handleToggle = useCallback((e) => {
    e.stopPropagation();
    setPlaying((p) => !p);
  }, []);

  if (!gifSrc && !thumbSrc) {
    return (
      <div className={className}>
        <PlaceholderThumb track={project.track} title={project.title} />
      </div>
    );
  }

  if (!isGif) {
    return (
      <div className={className}>
        <img src={thumbSrc} alt={project.title} onError={(e) => { e.target.style.display = 'none'; }} />
      </div>
    );
  }

  return (
    <div className={className}>
      {playing ? (
        <img src={gifSrc} alt={project.title} />
      ) : firstFrameUrl ? (
        <img src={firstFrameUrl} alt={project.title} />
      ) : (
        <PlaceholderThumb track={project.track} title={project.title} />
      )}
      <button className="app-gif-toggle" onClick={handleToggle} title={playing ? 'Stop' : 'Play'}>
        {playing ? <FiSquare /> : <FiPlay />}
      </button>
    </div>
  );
};

/* ── Detailed Read More content (used inside popup window) ── */
export const ProjectDetailContent = ({ projectId }) => {
  const [project, setProject] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    fetch(`/projects/${projectId}/project.json`)
      .then((r) => r.json())
      .then(setProject)
      .catch(() => {});
  }, [projectId]);

  if (!project) return <p style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>Loading…</p>;

  const githubLinks = Array.isArray(project.github) ? project.github : [];
  const sections = (project.detailed || '').split('\n\n');

  return (
    <div className="app-project-readmore">
      <ProjectMedia project={project} className="app-readmore-media" />

      <div className="app-readmore-header">
        <h2 className="app-readmore-title">{project.title}</h2>
        <TrackBadge track={project.track} />
      </div>

      <div className="app-readmore-body">
        {sections.map((para, i) => {
          if (/^(Problem|Solution|How It Works|Key Innovation)$/i.test(para.trim())) {
            return <h3 key={i} className="app-readmore-section-title">{para.trim()}</h3>;
          }
          return <p key={i} className="app-readmore-para">{para}</p>;
        })}
      </div>

      {(project.highlights || []).length > 0 && (
        <div className="app-highlights-grid">
          {project.highlights.map((h, i) => (
            <div key={i} className="app-highlight-chip">
              <span className="app-highlight-label">{h.label}</span>
              <span className="app-highlight-value">{h.value}</span>
            </div>
          ))}
        </div>
      )}

      <div className="app-detail-tags" style={{ marginTop: 16 }}>
        {(project.skills || []).map((s, i) => (
          <span key={i} className="app-project-tag">{s}</span>
        ))}
      </div>

      <div className="app-detail-links" style={{ marginTop: 12 }}>
        {githubLinks.map((g, i) => (
          <a key={i} href={g.url} target="_blank" rel="noreferrer" className="app-detail-link">
            <VscGithubInverted className="app-link-icon" />{g.label}
          </a>
        ))}
        {project.youtubeUrl && (
          <a href={project.youtubeUrl} target="_blank" rel="noreferrer" className="app-detail-link app-detail-link--yt">
            <FiYoutube className="app-link-icon" />Demo Video
          </a>
        )}
      </div>
    </div>
  );
};

/* ── Main ProjectsApp ── */
const ProjectsApp = () => {
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [trackFilter, setTrackFilter] = useState('all');
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);

  useEffect(() => {
    fetch('/projects/metadata.json')
      .then((r) => r.json())
      .then((data) => setProjects(data.projects || []))
      .catch(() => {});
  }, []);

  const tracks = useMemo(() => {
    const set = new Set(projects.map((p) => p.track));
    return ['all', ...Array.from(set).sort()];
  }, [projects]);

  const filtered = useMemo(() => {
    let list = projects;
    if (trackFilter !== 'all') list = list.filter((p) => p.track === trackFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) =>
        p.title.toLowerCase().includes(q) ||
        p.shortDescription.toLowerCase().includes(q) ||
        (p.skills || []).some((s) => s.toLowerCase().includes(q))
      );
    }
    return list;
  }, [projects, trackFilter, search]);

  const selectProject = useCallback((stub) => {
    fetch(`/projects/${stub.id}/project.json`)
      .then((r) => r.json())
      .then((full) => setSelected(full))
      .catch(() => setSelected(stub));
  }, []);

  const openReadMore = useCallback(() => {
    if (!selected) return;
    if (window.__desktopOpenProjectDetail) {
      window.__desktopOpenProjectDetail(selected.id, selected.title);
    }
  }, [selected]);

  /* ── Overview (project selected) ── */
  if (selected) {
    const githubLinks = Array.isArray(selected.github) ? selected.github : [];
    return (
      <div className="app-project-overview">
        <button className="app-detail-back" onClick={() => setSelected(null)}>
          ← Back to Projects
        </button>

        <ProjectMedia project={selected} className="app-overview-thumb" />

        <div className="app-overview-titlerow">
          <h2 className="app-detail-title">{selected.title}</h2>
          <TrackBadge track={selected.track} />
        </div>

        <p className="app-overview-text">{selected.overview}</p>

        {(selected.highlights || []).length > 0 && (
          <div className="app-highlights-grid">
            {selected.highlights.map((h, i) => (
              <div key={i} className="app-highlight-chip">
                <span className="app-highlight-label">{h.label}</span>
                <span className="app-highlight-value">{h.value}</span>
              </div>
            ))}
          </div>
        )}

        <div className="app-detail-tags">
          {(selected.skills || []).slice(0, 8).map((s, i) => (
            <span key={i} className="app-project-tag">{s}</span>
          ))}
        </div>

        <div className="app-detail-links">
          {githubLinks.map((g, i) => (
            <a key={i} href={g.url} target="_blank" rel="noreferrer" className="app-detail-link">
              <VscGithubInverted className="app-link-icon" />{g.label}
            </a>
          ))}
          {selected.youtubeUrl && (
            <a href={selected.youtubeUrl} target="_blank" rel="noreferrer" className="app-detail-link app-detail-link--yt">
              <FiYoutube className="app-link-icon" />Demo Video
            </a>
          )}
        </div>

        {selected.detailed && (
          <button className="app-readmore-btn" onClick={openReadMore}>
            Read More →
          </button>
        )}
      </div>
    );
  }

  /* ── Grid view ── */
  return (
    <div className="app-projects-layout" onPointerDown={() => setCurrentlyPlaying(null)}>
      <div className="app-projects-toolbar">
        <input
          className="app-projects-search"
          type="text"
          placeholder="Search projects or skills…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="app-projects-filters">
          {tracks.map((t) => {
            const meta = TRACK_META[t];
            const isAll = t === 'all';
            const active = trackFilter === t;
            return (
              <button
                key={t}
                className={`app-filter-pill${active ? ' active' : ''}`}
                style={active && !isAll ? { background: meta?.color + '22', color: meta?.color, borderColor: meta?.color + '44' } : {}}
                onClick={() => setTrackFilter(t)}
              >
                {isAll ? 'All' : meta?.label || t}
              </button>
            );
          })}
        </div>
      </div>

      <div className="app-projects-count">{filtered.length} project{filtered.length !== 1 ? 's' : ''}</div>

      <div className="app-projects-grid">
        {filtered.map((p) => (
          <div key={p.id} className="app-project-card" onClick={() => selectProject(p)}>
            <GridMedia project={p} className="app-project-thumb" currentlyPlaying={currentlyPlaying} setCurrentlyPlaying={setCurrentlyPlaying} />
            <div className="app-card-body">
              <div className="app-card-titlerow">
                <h3 className="app-project-name">{p.title}</h3>
                {p.featured && <span className="app-featured-dot" title="Featured" />}
              </div>
              <TrackBadge track={p.track} />
              <p className="app-project-tagline">{p.shortDescription}</p>
              <div className="app-project-tags">
                {(p.skills || []).slice(0, 3).map((s, i) => (
                  <span key={i} className="app-project-tag">{s}</span>
                ))}
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <p className="app-projects-empty">No projects match your search.</p>
        )}
      </div>
    </div>
  );
};

export default ProjectsApp;
